import { useEffect, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useVariableLockHub } from 'services/signalR/signalRClients';

const locksChangedDebounceMs = 200;

export const useVariableLockSync = (
  variableIds: number[],
  revisionId: number,
  invalidateQueries: () => Promise<unknown>,
) => {
  const variableIdsRef = useRef(variableIds);
  variableIdsRef.current = variableIds;

  const invalidateQueriesRef = useRef(invalidateQueries);
  invalidateQueriesRef.current = invalidateQueries;

  const onVariableLocksChanged = useDebouncedCallback(() => {
    void invalidateQueriesRef.current();
  }, locksChangedDebounceMs);

  useEffect(
    () => () => onVariableLocksChanged.cancel(),
    [onVariableLocksChanged],
  );

  const { invoker, isConnected } = useVariableLockHub(
    { VariableLocksChanged: onVariableLocksChanged },
    variableIds.length > 0,
  );

  const idsKey = variableIds.join(',');

  useEffect(() => {
    const ids = variableIdsRef.current;
    if (!isConnected || ids.length === 0) return;

    void invoker.SubscribeToVariableLocks(ids, revisionId);

    return () => {
      invoker.UnsubscribeFromVariableLocks(ids, revisionId);
    };
  }, [isConnected, revisionId, idsKey]);
};
